import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Checkbox from "../common/Checkbox";
import {
  IoIosAddCircleOutline,
  IoIosRemoveCircleOutline,
} from "react-icons/io";

// ─────────────────────────────
// ✅ Main Component
// ─────────────────────────────
export default function SmoothNestedAccordion() {
  const [accordionData, setAccordionData] = useState(data);
  return <NestedAccordion data={accordionData} setData={setAccordionData} />;
}

// ─────────────────────────────
// ✅ Nested Accordion
// ─────────────────────────────
const NestedAccordion = ({ data, setData }) => {
  const [openItems, setOpenItems] = useState({});
  // ✅ Open all items by default on mount
  useEffect(() => {
    const getAllIds = (items) => {
      let ids = {};
      items.forEach((item) => {
        ids[item.id] = true;
        if (item.children && item.children.length > 0) {
          ids = { ...ids, ...getAllIds(item.children) };
        }
      });
      return ids;
    };
    setOpenItems(getAllIds(data));
  }, [data]);

  const toggleOpen = (id) => {
    setOpenItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Recursive update for parent-child checkbox logic
  const toggleCheck = (itemId, checked, items) => {
    return items.map((item) => {
      if (item.id === itemId) {
        // Toggle this item
        const updatedItem = {
          ...item,
          checked,
          children: item.children
            ? item.children.map((child) => ({
                ...child,
                checked,
              }))
            : [],
        };
        return updatedItem;
      }

      // Recursive for nested children
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: toggleCheck(itemId, checked, item.children),
          checked:
            item.children.every((c) => c.checked) || (item.checked && checked),
        };
      }

      return item;
    });
  };

  const handleCheckboxChange = (id, checked) => {
    const updated = toggleCheck(id, checked, data);
    setData([...updated]);
  };

  return (
    <div className="bg-gray-100 text-[#242424] py-2 w-full flex-1 ">
      <div className="inline-block min-w-full">
        {data.map((item) => (
          <AccordionItem
            key={item.id}
            item={item}
            openItems={openItems}
            toggleOpen={toggleOpen}
            onCheck={handleCheckboxChange}
            level={0}
          />
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────
// ✅ Accordion Item Component
// ─────────────────────────────
const AccordionItem = ({ item, openItems, toggleOpen, onCheck, level }) => {
  const isOpen = openItems[item.id];
  const hasChildren = item.children && item.children.length > 0;
  const isLeaf = !hasChildren;

  return (
    <div className="mb-1">
      <div
        className={`flex items-center justify-between cursor-pointer py-1 px-2  ${
          level === 0 ? "font-bold" : isLeaf ? "font-normal" : "font-semibold"
        }`}
        onClick={() => hasChildren && toggleOpen(item.id)}
      >
        <div className="flex items-center gap-1.5">
          {hasChildren && (
            <div>
              {isOpen ? (
                <IoIosRemoveCircleOutline size={16} />
              ) : (
                <IoIosAddCircleOutline size={16} />
              )}
            </div>
          )}

          <Checkbox
            color="#1C1B1F"
            borderColor="border-[#1C1B1F]"
            checked={item.checked}
            onChange={(e) => onCheck(item.id, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            boxSize="w-3 h-3"
            iconSize={9}
          />

          <span className="text-[11px]">{item.label}</span>
        </div>
      </div>

      {/* Animate nested items */}
      <AnimatePresence>
        {isOpen && hasChildren && (
          <div className="pl-8 ml-3 overflow-hidden">
            {item.children.map((child) => (
              <AccordionItem
                key={child.id}
                item={child}
                openItems={openItems}
                toggleOpen={toggleOpen}
                onCheck={onCheck}
                level={level + 1}
              />
            ))}
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─────────────────────────────
// ✅ Data
// ─────────────────────────────
export const data = [
  {
    id: "all",
    label: "ALL",
    checked: true,
    children: [
      {
        id: "A",
        label: "A Architecture",
        checked: true,
        children: [],
      },
      {
        id: "C",
        label: "C CIVIL",
        checked: true,
        children: [
          { id: "C0", label: "C0 CIVIL", checked: true },
          { id: "C2", label: "C2 Mudmat & Water pr", checked: true },
          { id: "C3", label: "C0 CIVIL", checked: true },
          { id: "C4", label: "C0 CIVIL", checked: true },
        ],
      },
      {
        id: "E",
        label: "E Electrical",
        checked: false,
        children: [],
      },
      {
        id: "H",
        label: "H HVAC",
        checked: true,
        children: [
          { id: "H0", label: "H0 General", checked: true },
          { id: "H1", label: "H1 HVAC Equipment", checked: true },
          { id: "H2", label: "H2 HVAC Equipment", checked: true },
        ],
      },
      {
        id: "J",
        label: "J Instrument & Control",
        checked: false,
        children: [],
      },
      {
        id: "HH",
        label: "H HVAC (duplicate)",
        checked: true,
        children: [
          { id: "HH0", label: "H0 General", checked: true },
          { id: "HH1", label: "H1 HVAC Equipment", checked: true },
        ],
      },
      {
        id: "K",
        label: "K Electrical Bulks",
        checked: true,
        children: [
          { id: "K1", label: "K1 Cable Tray & Suppo", checked: true },
          { id: "K2", label: "K2 Conduit", checked: true },
        ],
      },
    ],
  },
];
